import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Chip,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { AppAutocomplete, AppButton, AppTextField } from "../../../components/form";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import DataTable from "react-data-table-component";
import {
  AppDialog,
  AppDialogActions,
  AppDialogCancelButton,
  AppDialogContent,
  AppDialogTitle,
} from "../../../components/dialog";
import {
  AppFormField,
  AppFormGrid,
  PageDataTablePanel,
  PageFilterPanel,
  PageStatCard,
} from "../../../components/layout";
import { PageEmptyState, PageHeader, PageListContainer } from "../../../components/Page";
import { auditService, AUDIT_ACTIONS, type AuditAction, type AuditEvent } from "../../../services/auditService";
import { useMaterialUIController } from "../../../context";
import { GRID_FILTER_3, GRID_THIRD, GRID_TWO_THIRDS, pageStatGridSx } from "../../../lib/ui/formLayout";
import {
  getReportChatShell,
} from "../../../lib/ui/reportChatShell";
import { useAppCustomStyles } from "../../../assets/theme/custom/hooks";
import { buildAuditLogDataTableCustomStyles } from "../../../assets/theme/custom/components/report";

type AuditActionOption = {
  value: AuditAction | null;
  label: string;
};

const toIsoUtc = (value: string) => {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
};

const formatOccurredAt = (value?: string) =>
  value ? new Date(value).toISOString().replace("T", " ").slice(0, 19) : "-";

const formatMetadataPretty = (metadata?: Record<string, unknown>) => {
  if (!metadata || !Object.keys(metadata).length) return null;
  try {
    return JSON.stringify(metadata, null, 2);
  } catch {
    return null;
  }
};

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <Stack spacing={0.25}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
        {value || "-"}
      </Typography>
    </Stack>
  );
}

function AuditLogPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const custom = useAppCustomStyles();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [actionFilter, setActionFilter] = useState<AuditAction | null>(null);
  const [embedOnly, setEmbedOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [searched, setSearched] = useState(false);
  const [selected, setSelected] = useState<AuditEvent | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await auditService.list(
        toIsoUtc(from),
        toIsoUtc(to),
        actionFilter || undefined,
        embedOnly ? "embed_config" : undefined
      );
      setEvents(response.content || []);
      setSearched(true);
    } catch (e: any) {
      console.error(e);
      setError(e?.response?.data?.message || t("audit.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const actionFilterOptions = useMemo<AuditActionOption[]>(
    () => [
      { value: null, label: t("audit.allActions") },
      ...AUDIT_ACTIONS.map((action) => ({
        value: action,
        label: t(`audit.action.${action}`, { defaultValue: action }),
      })),
    ],
    [t]
  );

  const selectedActionOption =
    actionFilterOptions.find((option) => option.value === actionFilter) ?? actionFilterOptions[0];

  const actionSummary = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of events) {
      const key = item.action || "UNKNOWN";
      map.set(key, (map.get(key) || 0) + 1);
    }
    return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, 4);
  }, [events]);

  const reportShell = useMemo(() => getReportChatShell(theme), [theme]);
  const tableShellStyles = useMemo(() => buildAuditLogDataTableCustomStyles(theme), [theme]);

  const columns = useMemo(
    () => [
      {
        id: "occurredAt",
        name: t("audit.colTime"),
        selector: (row: AuditEvent) => formatOccurredAt(row.occurredAt),
        sortable: true,
        sortFunction: (a: AuditEvent, b: AuditEvent) => {
          const ta = a.occurredAt ? new Date(a.occurredAt).getTime() : 0;
          const tb = b.occurredAt ? new Date(b.occurredAt).getTime() : 0;
          return ta - tb;
        },
        minWidth: "170px",
      },
      {
        name: t("audit.colAction"),
        selector: (row: AuditEvent) =>
          row.action ? t(`audit.action.${row.action}`, { defaultValue: row.action }) : "-",
        sortable: false,
        minWidth: "180px",
      },
      {
        name: t("audit.colUser"),
        selector: (row: AuditEvent) => row.actorUsername || "-",
        sortable: false,
        minWidth: "140px",
      },
      {
        name: t("audit.colResourceType"),
        selector: (row: AuditEvent) => row.resourceType || "-",
        sortable: false,
        minWidth: "120px",
        grow: 1,
      },
    ],
    [t]
  );

  const selectedMetadata = formatMetadataPretty(selected?.metadata);
  const selectedActionLabel = selected?.action
    ? t(`audit.action.${selected.action}`, { defaultValue: selected.action })
    : "-";

  return (
    <PageListContainer surface="plain">
      <PageHeader
        title={t("audit.title")}
        subtitle={t("audit.subtitle")}
        actions={
          <AppButton
            variant="contained"
            color="dark"
            size="small"
            onClick={load}
            disabled={loading}
          >
            {loading ? t("common.loading") : t("audit.load")}
          </AppButton>
        }
      />

      <PageFilterPanel>
        <AppFormField size={GRID_FILTER_3}>
          <AppTextField
            label={t("dateRange.from")}
            type="datetime-local"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </AppFormField>
        <AppFormField size={GRID_FILTER_3}>
          <AppTextField
            label={t("dateRange.to")}
            type="datetime-local"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </AppFormField>
        <AppFormField size={GRID_FILTER_3}>
          <AppAutocomplete<AuditActionOption>
            label={t("audit.filterAction")}
            options={actionFilterOptions}
            value={selectedActionOption}
            onChange={(_, option) => setActionFilter(option?.value ?? null)}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            disableClearable
          />
        </AppFormField>
        <AppFormField size={GRID_FILTER_3}>
          <FormControlLabel
            control={<Checkbox checked={embedOnly} onChange={(_, checked) => setEmbedOnly(checked)} />}
            label={t("audit.embedOnly")}
          />
        </AppFormField>
      </PageFilterPanel>

      <AppFormGrid spacing="compact" sx={pageStatGridSx}>
        <AppFormField size={GRID_THIRD}>
          <PageStatCard label={t("audit.totalEvents")} value={events.length} />
        </AppFormField>
        <AppFormField size={GRID_TWO_THIRDS}>
          <PageStatCard label={t("audit.topActions")}>
            <Stack direction="row" gap={0.5} flexWrap="wrap" sx={custom.pages.shared.statChipStack}>
              {actionSummary.length ? actionSummary.map(([key, count]) => (
                <Chip
                  key={key}
                  size="small"
                  label={`${t(`audit.action.${key}`, { defaultValue: key })}: ${count}`}
                />
              )) : <Typography variant="body2">-</Typography>}
            </Stack>
          </PageStatCard>
        </AppFormField>
      </AppFormGrid>

      {error && <Alert severity="error" sx={custom.pages.shared.alertMarginBottom1_5}>{error}</Alert>}

      <PageDataTablePanel borderColor={reportShell.border} backgroundColor={reportShell.appBg}>
        <DataTable
          columns={columns as any}
          data={events}
          progressPending={loading}
          persistTableHead
          pagination
          paginationPerPage={50}
          paginationRowsPerPageOptions={[25, 50, 100, 200]}
          defaultSortFieldId="occurredAt"
          defaultSortAsc={false}
          highlightOnHover
          pointerOnHover
          onRowClicked={(row) => setSelected(row)}
          noDataComponent={
            searched ? (
              <PageEmptyState message={t("audit.noResults")} />
            ) : (
              <PageEmptyState message={t("audit.clickLoad")} />
            )
          }
          customStyles={tableShellStyles}
          theme={darkMode ? "dark" : "light"}
        />
      </PageDataTablePanel>

      <AppDialog
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        maxWidth="sm"
        fullWidth
      >
        <AppDialogTitle>{t("audit.detailTitle")}</AppDialogTitle>
        <AppDialogContent>
          {selected && (
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              <DetailField label={t("audit.colTime")} value={formatOccurredAt(selected.occurredAt)} />
              <DetailField label={t("audit.colAction")} value={selectedActionLabel} />
              <DetailField label={t("audit.colUser")} value={selected.actorUsername || "-"} />
              <DetailField label={t("audit.colResourceType")} value={selected.resourceType || "-"} />
              <DetailField label={t("audit.colResourceId")} value={selected.resourceId || "-"} />
              <DetailField label={t("audit.colTenant")} value={selected.tenantId || "-"} />
              <Stack spacing={0.25}>
                <Typography variant="caption" color="text.secondary">
                  {t("audit.colMetadata")}
                </Typography>
                {selectedMetadata ? (
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.5,
                      borderRadius: 1,
                      border: 1,
                      borderColor: "divider",
                      bgcolor: "action.hover",
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                      fontSize: 12,
                      lineHeight: 1.5,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      overflow: "auto",
                      maxHeight: 320,
                    }}
                  >
                    {selectedMetadata}
                  </Box>
                ) : (
                  <Typography variant="body2">-</Typography>
                )}
              </Stack>
            </Stack>
          )}
        </AppDialogContent>
        <AppDialogActions>
          <AppDialogCancelButton onClick={() => setSelected(null)}>
            {t("common.close")}
          </AppDialogCancelButton>
        </AppDialogActions>
      </AppDialog>
    </PageListContainer>
  );
}

export default AuditLogPage;
